import React, { ReactElement, useEffect, useMemo, useState } from "react";
import ReactDom from "react-dom";
import { Badge, Form, Tab, Tabs } from "react-bootstrap";
import { Column } from "react-table";
import { faUsers, faUserCheck, faUserSlash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable, Loading } from "../ui";
import { IUserTableData, UserService } from "./UserService";

// Custom dropdown filter for Yes/No columns
function SelectYesNoFilter({
	column: { filterValue, setFilter },
}: any) {
	return (
		<Form.Select
			size="sm"
			value={filterValue ?? ""}
			onChange={e => {
				setFilter(e.target.value || undefined);
			}}
			className="mt-1"
		>
			<option value="">All</option>
			<option value="true">Yes</option>
			<option value="false">No</option>
		</Form.Select>
	);
}

// Custom dropdown filter for Role column
function SelectRoleFilter({
	column: { filterValue, setFilter },
}: any) {
	return (
		<Form.Select
			size="sm"
			value={filterValue ?? ""}
			onChange={e => {
				setFilter(e.target.value || undefined);
			}}
			className="mt-1"
		>
			<option value="">All Roles</option>
			<option value="admin">Admin</option>
			<option value="manager">Manager</option>
			<option value="salesrep">Sales Rep</option>
			<option value="partner">Partner</option>
			<option value="basic">Basic</option>
		</Form.Select>
	);
}

// Custom filter function for boolean values
function booleanFilter(rows: any[], id: string, filterValue: string) {
	if (!filterValue) return rows;
	const boolValue = filterValue === "true";
	return rows.filter(row => row.values[id] === boolValue);
}

export const UsersListPage = (): ReactElement | null => {
	const [activeUsers, setActiveUsers] = useState<IUserTableData[]>([]);
	const [archivedUsers, setArchivedUsers] = useState<IUserTableData[]>([]);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState<string>("active");

	const userService = new UserService();

	useEffect(() => {
		const loadUsers = async () => {
			setLoading(true);
			const [active, archived] = await Promise.all([
				userService.getUsersForTable(true),
				userService.getUsersForTable(false),
			]);
			setActiveUsers(active);
			setArchivedUsers(archived);
			setLoading(false);
		};

		loadUsers();
	}, []);

	const columns: Column<IUserTableData>[] = useMemo(
		() => [
			{
				Header: "Name",
				accessor: "full_name",
				Cell: ({ row }: any) => (
					<a href={`/users/${row.original.id}`}>
						{row.original.full_name}
						<span className="text-muted ms-1">({row.original.leads_count})</span>
					</a>
				),
			},
			{
				Header: "Email",
				accessor: "email",
				Cell: ({ value }: any) => (
					<a href={`mailto:${value}`}>{value}</a>
				),
			},
			{
				Header: "Phone",
				accessor: "phone",
				Cell: ({ value }: any) => value || "-",
			},
			{
				Header: "Role",
				accessor: "role",
				Filter: SelectRoleFilter,
				Cell: ({ value }: any) => (
					<Badge bg={getRoleBadgeColor(value)} className="text-capitalize">
						{value}
					</Badge>
				),
			},
			{
				Header: "Round Robin",
				accessor: "is_in_roundrobin",
				Filter: SelectYesNoFilter,
				filter: booleanFilter,
				Cell: ({ value }: any) => (
					<Badge bg={value ? "success" : "secondary"}>
						{value ? "Yes" : "No"}
					</Badge>
				),
			},
		],
		[],
	);

	const getRoleBadgeColor = (role: string): string => {
		switch (role) {
			case "admin":
				return "purple";
			case "manager":
				return "info";
			case "salesrep":
				return "primary";
			case "partner":
				return "warning";
			default:
				return "secondary";
		}
	};

	const renderTable = (users: IUserTableData[]) => {
		if (loading) {
			return <Loading />;
		}

		if (users.length === 0) {
			return (
				<div className="text-muted text-center py-4">
					No users found.
				</div>
			);
		}

		return <DataTable columns={columns} data={users} />;
	};

	const output = (
		<div id="users-list-page">
			<div className="datatable-card">
				<div className="datatable-header">
					<div className="datatable-title">
						<FontAwesomeIcon icon={faUsers} />
						Team Members
					</div>
					<div className="datatable-actions">
						<a href="/users/create" className="btn btn-users btn-sm">
							+ Create User
						</a>
					</div>
				</div>
				<div className="datatable-body">
					<Tabs
						activeKey={activeTab}
						onSelect={(k) => setActiveTab(k || "active")}
						className="mb-3"
					>
						<Tab
							eventKey="active"
							title={
								<>
									<FontAwesomeIcon icon={faUserCheck} className="me-1" />
									Active ({activeUsers.length})
								</>
							}
						>
							{renderTable(activeUsers)}
						</Tab>
						<Tab
							eventKey="archived"
							title={
								<>
									<FontAwesomeIcon icon={faUserSlash} className="me-1" />
									Archived ({archivedUsers.length})
								</>
							}
						>
							{renderTable(archivedUsers)}
						</Tab>
					</Tabs>
				</div>
			</div>
		</div>
	);

	const portal = document.getElementById("users-list-portal");
	if (!portal) {
		return null;
	}

	return ReactDom.createPortal(output, portal);
};
